import type { AxiosError, AxiosInstance, AxiosRequestConfig } from "axios";
import axios from "axios";
import type { CognitoUser } from "@aws-amplify/auth";
import { ExceptionStatusCode } from "../../utils/Constants";
import { Configuration as BaseConfiguration } from "../backend_client/configuration";

interface QueueItem {
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}

class ExtendedConfiguration extends BaseConfiguration {
  axios?: AxiosInstance;
}

export const createAxiosInstance = (
  baseURL: string,
  authOptions: {
    getJwtToken?: () => Promise<string>;
    resetJwtToken?: () => void;
    getCognitoUser?: () => CognitoUser;
  } = {},
): { config: ExtendedConfiguration; axiosInstance: AxiosInstance } => {
  const instance = axios.create({
    baseURL,
    headers: {
      "Content-type": "application/json",
    },
  });

  let isRefreshing = false;
  let failedQueue: QueueItem[] = [];

  const processQueue = (error: Error | null = null) => {
    failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve();
      }
    });
    failedQueue = [];
  };

  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };
      if (error.response?.status === ExceptionStatusCode.UnauthorizedException && !originalRequest._retry) {
        if (isRefreshing) {
          return new Promise<unknown>((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then(() => {
              return instance(originalRequest);
            })
            .catch((err) => {
              return Promise.reject(err);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          if (authOptions.resetJwtToken) {
            authOptions.resetJwtToken();
          }

          const cognitoUser = authOptions.getCognitoUser?.();
          if (!cognitoUser) {
            throw new Error("No Cognito user available");
          }

          const currentSession = cognitoUser.getSignInUserSession();
          const refreshToken = currentSession?.getRefreshToken();

          if (!refreshToken) {
            throw new Error("No refresh token available");
          }

          return await new Promise((resolve, reject) => {
            cognitoUser.refreshSession(refreshToken, (err, session) => {
              if (err) {
                processQueue(err);
                reject(err);
                return;
              }

              const newToken = session.getIdToken().getJwtToken();
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = newToken;
              }

              processQueue();
              resolve(instance(originalRequest));
            });
          });
        } catch (refreshError) {
          processQueue(refreshError instanceof Error ? refreshError : new Error("Token refresh failed"));
          throw refreshError;
        } finally {
          isRefreshing = false;
        }
      }
      return Promise.reject(error);
    },
  );

  const config = new ExtendedConfiguration({
    apiKey: authOptions.getJwtToken,
    basePath: baseURL,
  });

  config.axios = instance;

  return { config, axiosInstance: instance };
};
